
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { LooseObject } from '@/pages/appstore/entities/app'
import Draggable from 'vuedraggable'
import { debounce } from '@/utils/function'

@Component<DraggableCarousel>({
    components: {
        Draggable,
    },
})
export default class DraggableCarousel extends Vue {
    public currentItems: LooseObject[] = []
    public beingDragged = false
    public pageItemNumber = 4
    public currentXPosition = 0
    public currentMouseDown = false
    public nextNumber = 0
    public previousNumber = 0
    public uniqueId: number = Math.random()
    public container: any

    @Prop({ type: String, required: true })
    public readonly type!: string
    @Prop({ type: Array, required: false })
    public readonly items!: LooseObject[]
    @Prop({ type: Boolean, required: false })
    public readonly isDraggable?: boolean
    @Prop({ type: Boolean, required: false })
    public readonly hasTilePlaceholder?: boolean // for App Store only
    @Prop({ type: Boolean, required: false })
    public readonly showTilePlaceholder?: boolean // for App Store only

    private debouncedSetPageNumber = debounce(this.setPageNumber, 400, false)
    private debouncedSetPreviousItem = debounce(
        this.setPreviousItem,
        200,
        false
    )
    private debouncedSetNextItem = debounce(this.setNextItem, 200, false)

    @Watch('showTilePlaceholder') // for App Store storybook only
    public watchShowTilePlaceholder() {
        this.setNextNumber()
    }

    @Watch('type')
    public watchType() {
        this.setPageNumber()
        this.setNextNumber()
    }

    @Watch('items')
    public watchItems() {
        this.currentItems = this.items || []
        this.setNextNumber()
    }

    public changeItemsOrder() {
        this.beingDragged = false
        this.$emit('changeItemsOrder', this.currentItems)
    }

    private setPageNumber() {
        const wWidth = window.innerWidth
        if (this.type === 'draggable_carousel_max_four_items') {
            this.pageItemNumber =
                wWidth < 835 ? 1 : wWidth < 1070 ? 2 : wWidth < 1350 ? 3 : 4
        } else if (this.type === 'draggable_carousel_max_two_items') {
            this.pageItemNumber = wWidth < 960 ? 1 : 2
        }
        this.setNextNumber()
    }

    private setNextNumber() {
        // this is the default condition
        if (!this.hasTilePlaceholder) {
            return (this.nextNumber =
                this.currentItems.length - this.pageItemNumber)
        }
        // for App Store only
        if (this.showTilePlaceholder) {
            // when the fist item is placeholder and need be showed
            this.nextNumber = this.currentItems.length - this.pageItemNumber
        } else {
            // when the fist item is placeholder and need be hidden
            this.nextNumber = this.currentItems.length - this.pageItemNumber - 1
        }
    }

    private setPreviousItem() {
        if (this.previousNumber > 0) {
            this.container!.scrollBy({
                left: -(
                    this.container!.querySelector(`.${this.type}`)!
                        .clientWidth || 0
                ),
                behavior: 'smooth',
            })
            this.nextNumber++
            this.previousNumber--
        }
    }

    private setNextItem() {
        if (this.nextNumber > 0) {
            this.container!.scrollBy({
                left:
                    this.container!.querySelector(`.${this.type}`)!
                        .clientWidth || 0,
                behavior: 'smooth',
            })
            this.nextNumber--
            this.previousNumber++
        }
    }

    private mousedownSetter = (e: MouseEvent) => {
        this.currentXPosition = e.pageX
        this.currentMouseDown = true
    }

    private carouselScroller = (e: MouseEvent) => {
        if (this.currentMouseDown) {
            window.scrollBy(this.currentXPosition - e.pageX, 0)
        }
    }

    private mouseupSetter = (e: MouseEvent) => {
        this.currentMouseDown = false
    }

    private async mounted() {
        this.watchItems()
        this.setPageNumber()
        this.container = document.getElementById(`carousel_${this.uniqueId}`)
        window.addEventListener('resize', this.debouncedSetPageNumber)
        if (this.isDraggable) {
            this.container.addEventListener('mousedown', this.mousedownSetter)
            this.container.addEventListener('mousemove', this.carouselScroller)
            this.container.addEventListener('mouseup', this.mouseupSetter)
        }
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.debouncedSetPageNumber)
        if (this.isDraggable) {
            this.container.removeEventListener(
                'mousedown',
                this.mousedownSetter
            )
            this.container.removeEventListener(
                'mousemove',
                this.carouselScroller
            )
            this.container.removeEventListener('mouseup', this.mouseupSetter)
        }
    }
}
